import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { MdAdd, MdRemove, MdCheck, MdVisibility } from 'react-icons/md';
import type { RootState } from '../../../redux/store';
import { setPillowSize, setPillowFile, setPillowCount, setPillowFabricUrl } from '../../../redux/slices/customizationSlice';
import type { PillowSize } from '../../../types/customization.types';
import type { PillowConfig } from '../customizerConfig';
import SwatchCircle from '../shared/SwatchCircle';
import SwatchPreviewModal from '../shared/SwatchPreviewModal';
import { THEME, SPRING } from '../theme';

const PILLOW_IMAGE_PATH = '/assets/ralph_input_images/pillow.jpeg';

const SIZE_OPTIONS: { value: PillowSize; label: string }[] = [
  { value: 'small', label: 'S' },
  { value: 'medium', label: 'M' },
  { value: 'large', label: 'L' },
];

interface PillowSectionProps {
  /** Product-level pillow configuration. Defaults to original behaviour if omitted. */
  pillowConfig?: PillowConfig;
}

const PillowSection: React.FC<PillowSectionProps> = ({ pillowConfig }) => {
  const dispatch = useDispatch();
  const { pillowSize, pillowFile, pillowFabricUrl } = useSelector((s: RootState) => s.customization);

  // Resolve config with defaults (original behaviour)
  const showSize = pillowConfig?.showSize ?? true;
  const showImageAndCount = pillowConfig?.showImageAndCount ?? true;
  const fabricSwatchKey = pillowConfig?.fabricSwatchKey ?? 'seatFabrics';

  const fabricSwatches = useSelector((s: RootState) => (s.customization.swatches as any)[fabricSwatchKey] as string[] ?? []);

  const [localCount, setLocalCount] = useState(2);
  // When image/count is hidden, pillow is always implicitly selected
  const [pillowSelected, setPillowSelected] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Set default size on mount — always medium when showSize is false
  useEffect(() => {
    if (!pillowSize) dispatch(setPillowSize('medium'));
  }, []);

  // Force medium when size picker is hidden
  useEffect(() => {
    if (!showSize && pillowSize !== 'medium') {
      dispatch(setPillowSize('medium'));
    }
  }, [showSize, pillowSize]);

  // When deselected, clear the Redux state
  useEffect(() => {
    if (!pillowSelected && pillowFile) {
      dispatch(setPillowFile(null));
    }
  }, [pillowSelected]);

  useEffect(() => {
    if (!showImageAndCount) {
      // Fabric-only mode: never load pillow file
      if (pillowFile) dispatch(setPillowFile(null));
      return;
    }
    if (pillowSelected && pillowFabricUrl) {
      (async () => {
        try {
          const res = await fetch(PILLOW_IMAGE_PATH);
          const blob = await res.blob();
          const file = new File([blob], 'pillow.jpeg', { type: blob.type || 'image/jpeg' });
          dispatch(setPillowFile(file));
        } catch {
          console.warn('[PillowSection] Could not load pillow image');
        }
      })();
    } else if (!pillowFabricUrl && pillowFile) {
      dispatch(setPillowFile(null));
    }
  }, [pillowSelected, pillowFabricUrl, showImageAndCount]);

  // Auto-sync count to Redux
  useEffect(() => {
    dispatch(setPillowCount(localCount));
  }, [localCount, dispatch]);

  const currentSize = pillowSize || 'medium';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25 }}
      className="space-y-5"
    >
      {/* ── Row: Swatch + Size & Count (conditionally shown) ── */}
      {showImageAndCount && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[11px] font-bold uppercase tracking-wider text-white/40">Pillow</span>
            <div className="flex-1 h-px" style={{ background: THEME.border }} />
          </div>

          <div className="flex items-start gap-6">
            {/* Square swatch */}
            <motion.div
              className="flex flex-col items-center gap-1.5 flex-shrink-0"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onHoverStart={() => setHovered(true)}
              onHoverEnd={() => setHovered(false)}
            >
              <button
                onClick={() => setPillowSelected(!pillowSelected)}
                className="relative cursor-pointer group"
              >
                <div
                  className="rounded-2xl overflow-hidden transition-all duration-300"
                  style={{
                    width: 88,
                    height: 88,
                    boxShadow: pillowSelected ? `0 0 0 2.5px ${THEME.primary}, ${THEME.primaryGlow}` : 'none',
                    border: pillowSelected ? 'none' : '1.5px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <img
                    src={PILLOW_IMAGE_PATH}
                    alt="Pillow"
                    className={`w-full h-full object-cover transition-all duration-300 ${
                      pillowSelected ? 'brightness-100 scale-105' : 'brightness-65 group-hover:brightness-90'
                    }`}
                    loading="lazy"
                  />
                </div>
                {/* Eye icon */}
                <AnimatePresence>
                  {hovered && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={SPRING.snappy}
                      className="absolute -top-1.5 -right-1.5 z-20"
                    >
                      <motion.div
                        whileHover={{ scale: 1.15 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => { e.stopPropagation(); setPreviewOpen(true); }}
                        className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer"
                        style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', border: '1.5px solid rgba(255,255,255,0.25)', boxShadow: '0 2px 10px rgba(0,0,0,0.4)' }}
                      >
                        <MdVisibility size={13} className="text-white/90" />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Check */}
                <AnimatePresence>
                  {pillowSelected && (
                    <motion.div
                      initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                      transition={SPRING.snappy}
                      className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ background: THEME.primary, boxShadow: THEME.primaryGlow }}
                    >
                      <MdCheck size={12} className="text-white" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            </motion.div>

            {/* Right side: Size & Count in same row */}
            <div className="flex-1 flex items-start justify-between min-w-0 pt-1">
              {/* Size — conditionally shown */}
              {showSize && (
                <div className="flex-shrink-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-white/35 block mb-1.5">Size</span>
                  <div className="flex gap-1.5">
                    {SIZE_OPTIONS.map((opt) => {
                      const isActive = currentSize === opt.value;
                      return (
                        <motion.button
                          key={opt.value}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => dispatch(setPillowSize(opt.value))}
                          className="w-8 h-8 rounded-lg text-[11px] font-semibold cursor-pointer transition-all duration-200 flex items-center justify-center"
                          style={{
                            background: isActive ? THEME.primary : 'rgba(255,255,255,0.04)',
                            color: isActive ? '#fff' : 'rgba(255,255,255,0.45)',
                            border: `1px solid ${isActive ? THEME.primary : 'rgba(255,255,255,0.08)'}`,
                            boxShadow: isActive ? THEME.primaryGlow : 'none',
                          }}
                        >
                          {opt.label}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Count */}
              <div className="flex-shrink-0">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/35 block mb-1.5">Qty</span>
                <div className="flex items-center gap-1.5">
                  <motion.button
                    whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={() => setLocalCount(Math.max(1, localCount - 1))}
                    className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer"
                    style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${THEME.border}` }}
                  >
                    <MdRemove size={12} className="text-white/60" />
                  </motion.button>
                  <span className="text-sm font-bold text-white tabular-nums w-5 text-center">{localCount}</span>
                  <motion.button
                    whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={() => setLocalCount(Math.min(10, localCount + 1))}
                    className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer"
                    style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${THEME.border}` }}
                  >
                    <MdAdd size={12} className="text-white/60" />
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Fabric selection ──────────────────────────────────── */}
      <AnimatePresence>
        {pillowSelected && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="overflow-hidden"
          >
            <div className="space-y-4">
              {/* Pillow Fabric */}
              {fabricSwatches.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-white/40">Fabric</span>
                    <div className="flex-1 h-px" style={{ background: THEME.border }} />
                    {pillowFabricUrl && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: THEME.primaryBg, color: THEME.primaryLight, border: `1px solid ${THEME.primaryBorder}` }}
                      >
                        1 selected
                      </motion.span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2.5 ml-2 mb-2">
                    {fabricSwatches.slice(0, 8).map((url, i) => (
                      <SwatchCircle
                        key={i}
                        url={url}
                        selected={pillowFabricUrl === url}
                        onClick={() => dispatch(setPillowFabricUrl(pillowFabricUrl === url ? null : url))}
                        size={42}
                      />
                    ))}
                  </div>
                </div>
              )}

              {!pillowFabricUrl && (
                <p className="text-[10px] text-white/25 text-center">Select a fabric for pillow</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <SwatchPreviewModal isOpen={previewOpen} imageUrl={PILLOW_IMAGE_PATH} label="Classic Throw Pillow" onClose={() => setPreviewOpen(false)} />
    </motion.div>
  );
};

export default PillowSection;
